import { useState, useEffect, useCallback } from 'react';
import type { AvailabilityRecord, AvailabilityStatus, WeeklyPlan, PlanFeedback, PlanFeedbackSummary } from '../types';
import * as api from '../api/client';
import { getWeekDates } from '../utils/helpers';

export function useWeekAvailability(events: { id: string; date: string }[]) {
  const [availability, setAvailability] = useState<Record<string, AvailabilityRecord[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (events.length === 0) {
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const result: Record<string, AvailabilityRecord[]> = {};
      await Promise.all(events.map(async (event) => {
        const data = await api.getAvailability(event.id);
        result[event.id] = data.availability;
      }));
      setAvailability(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [events.map(e => e.id).join(',')]);

  useEffect(() => { refresh(); }, [refresh]);

  const setMyAvailability = useCallback(async (eventId: string, status: AvailabilityStatus) => {
    await api.setAvailability(eventId, status);
    await refresh();
  }, [refresh]);

  return { availability, loading, error, refresh, setMyAvailability };
}

export function useWeeklyPlan() {
  const [plan, setPlan] = useState<WeeklyPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const weekDates = getWeekDates();
  const weekStart = weekDates[0];

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const data = await api.getWeeklyPlan(weekStart);
      setPlan(data.plan);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { refresh(); }, [refresh]);

  const generate = useCallback(async () => {
    try {
      setGenerating(true);
      setError(null);
      const weekEnd = weekDates[6];
      const data = await api.generateWeeklyPlan(weekStart, weekEnd);
      setPlan(data.plan);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }, [weekStart, weekDates[6]]);

  const share = useCallback(async () => {
    if (!plan) return;
    try {
      setError(null);
      const data = await api.shareWeeklyPlan(plan.id);
      setPlan(data.plan);
    } catch (err: any) {
      setError(err.message);
    }
  }, [plan?.id]);

  const publish = useCallback(async () => {
    if (!plan) return;
    try {
      setError(null);
      const data = await api.publishWeeklyPlan(plan.id);
      setPlan(data.plan);
    } catch (err: any) {
      setError(err.message);
    }
  }, [plan?.id]);

  const editAssignment = useCallback(async (eventId: string, data: { pickupDriverId?: string | null; returnDriverId?: string | null }) => {
    if (!plan) return;
    try {
      setError(null);
      const result = await api.updatePlanAssignment(plan.id, eventId, data);
      setPlan(result.plan);
    } catch (err: any) {
      setError(err.message);
    }
  }, [plan?.id]);

  return { plan, loading, error, generating, refresh, generate, share, publish, editAssignment };
}

export function usePlanFeedback(planId: string | null) {
  const [feedback, setFeedback] = useState<PlanFeedback[]>([]);
  const [summary, setSummary] = useState<PlanFeedbackSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    if (!planId) return;
    try {
      setLoading(true);
      const data = await api.getPlanFeedback(planId);
      setFeedback(data.feedback);
      setSummary(data.summary);
    } catch {
      // silently fail — feedback is supplementary
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = useCallback(async (status: 'approved' | 'concerns', comment?: string) => {
    if (!planId) return;
    try {
      setSubmitting(true);
      await api.submitPlanFeedback(planId, status, comment);
      await refresh();
    } finally {
      setSubmitting(false);
    }
  }, [planId, refresh]);

  return { feedback, summary, loading, submitting, submit, refresh };
}
